import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, FileSpreadsheet, LineChart, ShieldCheck, Smartphone, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { btnGold, btnGhost } from "@/components/attendance/ui";

const FEATURES = [
  [Smartphone, "Simple mobile counting", "Ushers tap + / − for adults, children, visitors or your own categories — no app to install."],
  [Users, "Many ushers, one live total", "Assign ushers to zones and departments. Totals update live for everyone, without double-counting."],
  [FileSpreadsheet, "Excel reports, emailed", "Close the event and an Excel workbook is emailed to your pastor, trustee or nominated authority."],
  [LineChart, "Weekly, monthly & yearly trends", "Compare like-for-like services and see growth or decline at a glance."],
  [ShieldCheck, "Private by design", "Each organisation's data is kept separate. Usher links are personal and can be revoked anytime."],
];

export default function AttendanceLanding() {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [hasOrg, setHasOrg] = useState(false);
  useEffect(() => { api.get("/attendance/plans").then((r) => setPlan(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user) api.get("/attendance/org").then(() => setHasOrg(true)).catch(() => setHasOrg(false)); }, [user]);

  const primary = hasOrg ? ["/attendance/dashboard", "Open dashboard"] : ["/attendance/register", "Start 30-day free trial"];
  return (
    <div data-testid="attendance-landing">
      <section className="relative overflow-hidden border-b border-white/5">
        <img src="https://images.unsplash.com/photo-1511578314322-379afb476865?crop=entropy&cs=srgb&fm=jpg&q=75&w=1920" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/40" />
        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 py-24 lg:py-32 mkt-rise">
          <div className="label-eyebrow mb-5">HOH Attendance</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.03] max-w-3xl">
            Count every soul — <span className="italic text-[#D4AF37]">live, together</span>, from any phone.
          </h1>
          <p className="mt-6 text-neutral-300 text-base lg:text-lg max-w-2xl leading-relaxed">
            Real-time headcounts for church services, weddings, funerals, conferences and community events — with Excel reports and attendance trends.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={primary[0]} data-testid="att-landing-primary" className={`${btnGold} px-7 py-3.5`}>{primary[1]} <ArrowRight size={15} /></Link>
            {!user && <Link to="/login?next=/attendance/dashboard" data-testid="att-landing-signin" className={`${btnGhost} px-7 py-3.5`}>Sign in</Link>}
          </div>
        </div>
      </section>
      <section className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURES.map(([Icon, t, d]) => (
          <div key={t} className="luxury-card rounded-xl p-6">
            <Icon className="text-[#D4AF37]" size={22} />
            <h3 className="font-serif-display text-2xl text-white mt-4">{t}</h3>
            <p className="text-sm text-neutral-400 mt-2 leading-relaxed">{d}</p>
          </div>
        ))}
      </section>
      {plan?.plan && <Pricing plan={plan} cta={primary} />}
    </div>
  );
}

function Pricing({ plan, cta }) {
  const p = plan.plan;
  return (
    <section className="border-t border-white/5 bg-[#0D0D0D]" data-testid="att-pricing">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="label-eyebrow mb-3">Simple pricing</div>
          <h2 className="font-serif-display text-3xl lg:text-5xl text-white">One plan. <span className="italic text-[#D4AF37]">Unlimited events.</span></h2>
          <ul className="mt-6 space-y-2 text-sm text-neutral-300">
            {["Unlimited events, ushers and zones", "Custom counting categories", "Excel reports + history export", `${plan.trial_days}-day free trial — cancel anytime`].map((x) => (
              <li key={x} className="flex gap-2"><CheckCircle2 size={16} className="text-[#D4AF37] shrink-0 mt-0.5" /> {x}</li>
            ))}
          </ul>
        </div>
        <div className="luxury-card rounded-2xl p-8">
          <div className="flex items-end gap-2"><span className="font-serif-display text-5xl text-white" data-testid="att-price-monthly">£{p.price_monthly.toFixed(2)}</span><span className="text-neutral-400 mb-2">/ month</span></div>
          <div className="text-sm text-neutral-400 mt-1" data-testid="att-price-annual">or £{p.price_annual.toFixed(2)} / year</div>
          <p className="text-xs text-neutral-500 mt-5 leading-relaxed">
            Your card is collected at sign-up. You won't be charged during the {plan.trial_days}-day trial; the first payment is taken on day {plan.trial_days + 1} unless you cancel. Renews automatically at the price shown. Cancel anytime from your billing page.
          </p>
          <Link to={cta[0]} className={`${btnGold} w-full mt-6 py-3.5`} data-testid="att-pricing-cta">{cta[1]}</Link>
        </div>
      </div>
    </section>
  );
}
